/* eslint-disable sonarjs/no-duplicate-string */
import { createSelector } from '@ngrx/store';

import { authQuery as authTokenQuery } from '@cigna/shared/angular/auth-data-access';

import { AccessToken } from '../auth/access-token.model';
import {
  Entitlement,
  EntitlementLob,
  JWT_NS,
  LOB,
  LOB_BEHAVIORAL,
  LOB_DENTAL,
  LOB_MEDBH,
  LOB_MEDICAL,
} from '@cigna/chcp/auth/util';

const selectFromAccess = createSelector(
  authTokenQuery.getAuthData,
  (authData) =>
    authData !== undefined
      ? (authData.accessTokenBody as AccessToken)
      : authData,
);

const getUserLob = createSelector(selectFromAccess, (accessToken) =>
  accessToken ? accessToken[`${JWT_NS}.lob`] : undefined,
);

const getAppName = createSelector(selectFromAccess, (accessToken) =>
  accessToken ? accessToken[`${JWT_NS}.appName`] : undefined,
);

const getUserLobId = createSelector(getUserLob, (lob) => {
  switch (lob) {
    case LOB_MEDBH: {
      return 1;
    }
    case LOB_DENTAL: {
      return 2;
    }
    default: {
      return 1;
    }
  }
});

const isDentalUser = createSelector(
  getUserLob,
  (lob) => lob && lob === LOB_DENTAL,
);

const isMedicalUser = createSelector(
  getUserLob,
  (lob) => lob && [LOB_MEDBH, LOB_MEDICAL].includes(lob),
);

const isBehUser = createSelector(
  getUserLob,
  (lob) => (lob && lob === LOB_BEHAVIORAL) || false,
);

const getLobHexApiPrefix = createSelector(
  getAppName,
  isDentalUser,
  (appName, isDental) =>
    appName === 'epp-web' ? '' : isDental ? 'dent' : 'med',
);

const getUserSub = createSelector(
  authTokenQuery.getAuthData,
  (authData) => (authData?.accessTokenBody as AccessToken)?.sub || null,
);

const getUserId = createSelector(selectFromAccess, (accessToken) =>
  accessToken ? accessToken[`${JWT_NS}.cn`] : undefined,
);

const getUserIdEncrypted = createSelector(selectFromAccess, (accessToken) =>
  accessToken ? accessToken[`${JWT_NS}.encryptedCn`] : undefined,
);

const authLevel = createSelector(selectFromAccess, (accessToken) =>
  accessToken ? accessToken[`${JWT_NS}.authLevel`] : undefined,
);

const operatorRole = createSelector(selectFromAccess, (accessToken) =>
  accessToken
    ? accessToken[`${JWT_NS}.operatorRole`] || accessToken['agent.operatorRole']
    : undefined,
);

const operatorId = createSelector(selectFromAccess, (accessToken) =>
  accessToken
    ? accessToken[`${JWT_NS}.operatorId`] || accessToken['agent.operatorId']
    : undefined,
);

const passwordExpiry = createSelector(selectFromAccess, (accessToken) =>
  accessToken ? accessToken[`${JWT_NS}.passwordExpiry`] : undefined,
);

const isImpersonator = createSelector(operatorRole, (role) => !!role);

const isRestrictedImpersonator = createSelector(
  operatorRole,
  (role: string | undefined) =>
    ['CHCP_RES', 'EPP_RES', 'IMP_LVL_1'].includes(role || '') || false,
);
const isRestrictedImpersonatorAuth0 = (props: { context: string | '' }) =>
  createSelector(operatorRole, (role: string | undefined) => {
    if (props.context === 'evernorth') {
      return role === 'G_EPP_IMPERSONATOR_RESTRICTED';
    }
    if (props.context === 'chcp') {
      return (
        role === 'G_WEBSUPPORT_CSALVL2_T' || role === 'G_WEBSUPPORT_CSALVL2_P'
      );
    }
    return false;
  });
const isUnRestrictedImpersonator = createSelector(
  operatorRole,
  (role: string | undefined) =>
    ['CHCP_UNRES', 'EPP_UNRES'].includes(role || '') || false,
);
const isUnRestrictedImpersonatorAuth0 = (props: { context: string | '' }) =>
  createSelector(operatorRole, (role: string | undefined) => {
    if (props.context === 'evernorth') {
      return role === 'G_EPP_IMPERSONATOR_UNRESTRICTED';
    }
    if (props.context === 'chcp') {
      return (
        role === 'G_WEBSUPPORT_CSALVL1_P' || role === 'G_WEBSUPPORT_CSALVL1_T'
      );
    }
    return false;
  });

const isChatAllowedImpersonator = createSelector(
  operatorRole,
  (role: string | undefined) =>
    ['CHCP_RES_OPS', 'EPP_RES_OPS'].includes(role || '') || false,
);

const isChatAllowedImpersonatorAuth0 = (props: { context: string | '' }) =>
  createSelector(operatorRole, (role: string | undefined) => {
    if (props.context === 'evernorth') {
      return (
        role === 'G_EPP_IMPER_RESTRICTED_OPS_T' ||
        role === 'G_EPP_IMPER_RESTRICTED_OPS_P'
      );
    }
    if (props.context === 'chcp') {
      return (
        role === 'G_CHCP_IMPER_RESTRICTED_OPS_T' ||
        role === 'G_CHCP_IMPER_RESTRICTED_OPS_P'
      );
    }
    return false;
  });

const isSalesDemoUser = createSelector(
  getUserId,
  (userid) => (userid && userid.startsWith('sales')) || false,
);

const selectUserAuthEntitlements = createSelector(
  selectFromAccess,
  (accessToken) => {
    const ent = accessToken?.[`${JWT_NS}.entitlements`];
    if (!ent) {
      return undefined;
    }
    if (Array.isArray(ent)) {
      return ent.slice().sort();
    }
    if (typeof ent === 'string') {
      return ent.split(' ').sort();
    }
    return undefined;
  },
);

const getUserAuthEntitlements = createSelector(
  selectUserAuthEntitlements,
  (entitlements) =>
    entitlements?.map((e: string) => {
      const ent = e.split('_');
      return ent[ent.length - 1];
    }),
);

const getPermissionToggles = createSelector(selectFromAccess, (accessToken) => {
  const toggles = accessToken?.[`${JWT_NS}.permissionToggles`];
  if (!toggles) {
    return undefined;
  }
  if (Array.isArray(toggles)) {
    return toggles;
  }
  if (typeof toggles === 'string') {
    return toggles.split(' ');
  }
  return undefined;
});

const hasAllEntitlements = (props: {
  entitlements: Array<Entitlement | EntitlementLob>;
}) =>
  createSelector(
    getUserAuthEntitlements,
    getUserLob,
    (userEntitlements, userLob) =>
      userEntitlements
        ? props.entitlements.every((v) => {
            // eslint-disable-next-line no-prototype-builtins
            if (v.hasOwnProperty('entitlement')) {
              return (
                userEntitlements.includes((v as EntitlementLob).entitlement) &&
                (v as EntitlementLob).lobs.includes(userLob as LOB)
              );
            }
            return userEntitlements.includes(v as Entitlement);
          })
        : false,
  );

const hasOneOfEntitlements = (props: {
  entitlements: Array<Entitlement | EntitlementLob>;
}) =>
  createSelector(
    getUserAuthEntitlements,
    getUserLob,
    (userEntitlements, userLob) =>
      userEntitlements
        ? // eslint-disable-next-line sonarjs/no-identical-functions
          props.entitlements.some((v) => {
            // eslint-disable-next-line no-prototype-builtins
            if (v.hasOwnProperty('entitlement')) {
              return (
                userEntitlements.includes((v as EntitlementLob).entitlement) &&
                (v as EntitlementLob).lobs.includes(userLob as LOB)
              );
            }
            return userEntitlements.includes(v as Entitlement);
          })
        : false,
  );

const isUserAuthenticated = createSelector(
  getUserLob,
  (lob) => lob !== undefined && lob !== null,
);

const isAuthLoading = authTokenQuery.isAuthLoading;

export const authQuery = {
  getUserLob,
  getUserLobId,
  getUserSub,
  getUserId,
  getUserIdEncrypted,
  isAuthLoading,
  isDentalUser,
  isMedicalUser,
  isBehUser,
  getUserAuthEntitlements,
  hasAllEntitlements,
  hasOneOfEntitlements,
  isUserAuthenticated,
  authLevel,
  operatorId,
  operatorRole,
  isImpersonator,
  isRestrictedImpersonator,
  isRestrictedImpersonatorAuth0,
  isUnRestrictedImpersonator,
  isUnRestrictedImpersonatorAuth0,
  isChatAllowedImpersonator,
  isChatAllowedImpersonatorAuth0,
  isSalesDemoUser,
  getPermissionToggles,
  getLobHexApiPrefix,
  passwordExpiry,
};
